import React, { useState, useEffect, useCallback } from 'react';
import Searchbar from '../../components/searchbar/searchbar';
import { useNavigate } from 'react-router-dom';

// Firebase imports
import { collection, getDocs, query, orderBy, where, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { getRedirectResult } from 'firebase/auth';

import HostDetailModal from '../../components/HostDetailModal';
import JoinRequestModal from '../../components/JoinRequestModal';
import RequestSentModal from '../../components/RequestSentModal';
import ImageModal from '../../components/ImageModal';
import PreviewProfileModal from '../../components/PreviewProfileModal';
import HostCard from '../../components/HostCard';
import FilterBar from '../../components/FilterBar';
import MapModal from '../../components/MapModal';
import './Home.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from '../../contexts/AuthContext';
import { createDinnerRequest } from '../../utils/firestoreRequests';
import { getUserProfile } from '../../utils/firestoreUser';
import { getCode as getCountryCode } from 'country-list';
import DinnersMap from '../../components/DinnersMap';
import Header from '../../components/Header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';

export default function Home({ showMobileFilterBar, setShowMobileFilterBar }) {
    const [dinners, setDinners] = useState([]);
    const [allDinnersData, setAllDinnersData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Searchbar state (these hold current input values, not active search criteria) ---
    const [location, setLocation] = useState(''); // Can be string or object from Google Places
    const [date, setDate] = useState(null);
    const [guests, setGuests] = useState(null);

    // --- Filter state (these hold current filter selections) ---
    const [currentMealStyles, setCurrentMealStyles] = useState(new Set());
    const [currentKosherOptions, setCurrentKosherOptions] = useState(new Set());

    // --- UI/Modal state ---
    const [modalState, setModalState] = useState('closed');
    const [selectedHost, setSelectedHost] = useState(null);
    const [selectedHostIndex, setSelectedHostIndex] = useState(-1); // New state for current dinner index
    const [sentRequests, setSentRequests] = useState(new Set());
    const [acceptedRequests, setAcceptedRequests] = useState(new Set());
    const [imageModalUrl, setImageModalUrl] = useState(null);
    const [previousModalState, setPreviousModalState] = useState('closed');
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [selectedProfileHost, setSelectedProfileHost] = useState(null);
    const [selectedProfileHostProfile, setSelectedProfileHostProfile] = useState(null);
    const [showMap, setShowMap] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);
    const [wasMapOpen, setWasMapOpen] = useState(false);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const { currentUser } = useAuth();
    const [userLanguages, setUserLanguages] = useState('');

    const navigate = useNavigate();

    // Helper function to calculate distance between two points (Haversine formula)
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Radius of the earth in km
        const toRad = (value) => (value * Math.PI) / 180;

        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in km
    };

    // Helper function to check if a date matches recurring pattern
    const isDateMatchingRecurring = (selectedDate, recurringOption) => {
        if (!recurringOption || !selectedDate) return false;

        const selectedDay = selectedDate.getDay(); // 5 for Friday
        if (selectedDay !== 5) return false;

        // For weekly recurring
        if (recurringOption === 'weekly') return true;

        // For biweekly recurring
        if (recurringOption === 'biweekly') {
            const weekNumber = Math.floor(selectedDate.getDate() / 7);
            return weekNumber % 2 === 0;
        }

        // For triweekly recurring
        if (recurringOption === 'triweekly') {
            const weekNumber = Math.floor(selectedDate.getDate() / 7);
            return weekNumber % 3 === 0;
        }

        // For monthly recurring
        if (recurringOption === 'monthly') {
            return selectedDate.getDate() <= 7; // First Friday of the month
        }

        return false;
    };

    // Helper function to get the Nth Friday of a given month and year
    const getNthFridayOfMonth = (year, month, n) => {
        let count = 0;
        for (let day = 1; day <= 31; day++) {
            let date = new Date(year, month, day, 12, 0, 0);
            if (date.getMonth() !== month) break;

            if (date.getDay() === 5) {
                count++;
                if (count === n) {
                    return date;
                }
            }
        }
        return null;
    };

    // Helper function to check if a dinner has any future dates
    const hasFutureDates = (dinner) => {
        const dinnerDetails = dinner.dinnerDetails || {};
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day

        // For recurring dinners, check if they have any future Fridays
        if (dinnerDetails.recurringOption) {
            // Check next 6 months for recurring dinners
            const checkUntil = new Date();
            checkUntil.setMonth(checkUntil.getMonth() + 6);

            let currentDate = new Date(today);

            while (currentDate <= checkUntil) {
                // Find the next Friday
                while (currentDate.getDay() !== 5) {
                    currentDate.setDate(currentDate.getDate() + 1);
                }

                if (currentDate >= today) {
                    return true;
                }

                // Move to next occurrence based on recurring option
                if (dinnerDetails.recurringOption === 'monthly') {
                    // For monthly, find the 4th Friday of the next month
                    let nextMonth = new Date(currentDate);
                    nextMonth.setMonth(currentDate.getMonth() + 1);
                    nextMonth.setDate(1); // Start from beginning of month

                    const nextFourthFriday = getNthFridayOfMonth(nextMonth.getFullYear(), nextMonth.getMonth(), 4);
                    if (nextFourthFriday) {
                        currentDate = nextFourthFriday;
                    } else {
                        // If no 4th Friday found, move to next month
                        currentDate.setMonth(currentDate.getMonth() + 1);
                        currentDate.setDate(1);
                    }
                } else {
                    // For weekly, biweekly, triweekly
                    const daysToAdd = {
                        'weekly': 7,
                        'biweekly': 14,
                        'triweekly': 21
                    }[dinnerDetails.recurringOption] || 7;

                    currentDate.setDate(currentDate.getDate() + daysToAdd);
                }
            }
            return false;
        }

        // For non-recurring dinners, check availableDates
        if (dinnerDetails.availableDates && Array.isArray(dinnerDetails.availableDates)) {
            return dinnerDetails.availableDates.some(dateStr => {
                const dinnerDate = new Date(dateStr);
                dinnerDate.setHours(0, 0, 0, 0);
                return dinnerDate >= today;
            });
        }

        // Fallback: check the main date field
        if (dinnerDetails.date) {
            const dinnerDate = new Date(dinnerDetails.date);
            dinnerDate.setHours(0, 0, 0, 0);
            return dinnerDate >= today;
        }

        return false;
    };

    // The main function to search and filter dinners based on provided criteria
    const searchDinners = useCallback((searchLocation, searchDate, searchGuests, filterMealStyles, filterKosherOptions) => {
        setLoading(true);
        setError(null);
        try {
            let filtered = allDinnersData.filter(dinner => {
                const dinnerDetails = dinner.dinnerDetails || {};
                const dinnerAddress = dinner.address || {};

                // 0. Filter out dinners created by the current user
                if (currentUser && dinner.hostId === currentUser.uid) {
                    return false;
                }

                // 0.5. Filter out dinners with no future dates
                if (!hasFutureDates(dinner)) {
                    return false;
                }

                // 1. Location matching - must match city/country or neighborhood/city/country
                // 1. Location matching - match by country if only country is provided, by city+country if both provided, or by all three if given
                if (searchLocation) {
                    let currentDinnerLocationMatches = true;
                    const searchLocParts = searchLocation.toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
                    const dinnerNeighborhood = (dinnerAddress.neighborhood || '').trim().toLowerCase();
                    const dinnerCity = (dinnerAddress.city || '').trim().toLowerCase();
                    const dinnerCountry = (dinnerAddress.country || '').trim().toLowerCase();

                    if (searchLocParts.length === 3) {
                        // Neighborhood, City, Country
                        const [searchNeighborhood, searchCity, searchCountry] = searchLocParts;
                        if (dinnerNeighborhood !== searchNeighborhood) currentDinnerLocationMatches = false;
                        if (dinnerCity !== searchCity) currentDinnerLocationMatches = false;
                        if (dinnerCountry !== searchCountry) currentDinnerLocationMatches = false;
                    } else if (searchLocParts.length === 2) {
                        // City, Country
                        const [searchCity, searchCountry] = searchLocParts;
                        if (dinnerCity !== searchCity) currentDinnerLocationMatches = false;
                        if (dinnerCountry !== searchCountry) currentDinnerLocationMatches = false;
                    } else if (searchLocParts.length === 1) {
                        // Only country or only city
                        const searchValue = searchLocParts[0];
                        // Try to match by country first, then by city if no country match
                        if (dinnerCountry === searchValue) {
                            currentDinnerLocationMatches = true;
                        } else if (dinnerCity === searchValue) {
                            currentDinnerLocationMatches = true;
                        } else {
                            currentDinnerLocationMatches = false;
                        }
                    } else {
                        // Fallback: substring match (legacy)
                        const dinnerLocationString = [dinnerAddress.neighborhood, dinnerAddress.city, dinnerAddress.country].filter(Boolean).join(', ').toLowerCase();
                        currentDinnerLocationMatches = searchLocParts.every(part => dinnerLocationString.includes(part));
                    }
                    if (!currentDinnerLocationMatches) {
                        return false;
                    }
                }

                // 2. Date matching - must match either specific date or recurring pattern, ONLY if searchDate is provided
                if (searchDate) {
                    // Set the search date to noon to avoid timezone issues
                    const searchDateNoon = new Date(searchDate);
                    searchDateNoon.setHours(12, 0, 0, 0);
                    const formattedSearchDate = searchDateNoon.toISOString().split('T')[0];
                    let dateMatches = false;

                    // 1. recurringOption (weekly, biweekly, etc.)
                    if (dinnerDetails.recurringOption) {
                        dateMatches = isDateMatchingRecurring(searchDateNoon, dinnerDetails.recurringOption);
                    }

                    // 2. availableDates (array of strings) - flexible check
                    if (!dateMatches && Array.isArray(dinnerDetails.availableDates)) {
                        dateMatches = dinnerDetails.availableDates.some(dateStr => {
                            if (!dateStr) return false;
                            try {
                                // Set dinner date to noon as well
                                const dinnerDate = new Date(dateStr);
                                dinnerDate.setHours(12, 0, 0, 0);
                                const dinnerDateFormatted = dinnerDate.toISOString().split('T')[0];
                                return dinnerDateFormatted === formattedSearchDate;
                            } catch (e) {
                                console.error('Error parsing date:', dateStr, e);
                                return false;
                            }
                        });
                    }

                    // 3. availableDates (string, e.g., "every friday")
                    if (!dateMatches && typeof dinnerDetails.availableDates === 'string') {
                        const dayOfWeek = searchDateNoon.getDay(); // 5 = Friday
                        if (dinnerDetails.availableDates.toLowerCase().includes('every friday') && dayOfWeek === 5) {
                            dateMatches = true;
                        }
                    }

                    // 4. date (single date field) - flexible check
                    if (!dateMatches && dinnerDetails.date) {
                        try {
                            // Set dinner date to noon as well
                            const dinnerDate = new Date(dinnerDetails.date);
                            dinnerDate.setHours(12, 0, 0, 0);
                            const dinnerDateFormatted = dinnerDate.toISOString().split('T')[0];
                            dateMatches = dinnerDateFormatted === formattedSearchDate;
                        } catch(e) {
                            console.error('Error parsing single date:', dinnerDetails.date, e);
                            dateMatches = false;
                        }
                    }

                    if (!dateMatches) return false;
                }

                // 3. Guest matching - must be equal to or greater than search criteria, ONLY if searchGuests is provided
                if (searchGuests) {
                    const maxGuests = dinnerDetails.maxGuests || 0;
                    if (!(maxGuests >= searchGuests)) return false;
                }

                // 4. Kosher filtering (exact match for single kosherType), ONLY if filterKosherOptions is provided
                if (filterKosherOptions.size > 0) {
                    const selectedKosher = Array.from(filterKosherOptions)[0];
                    if (dinnerDetails.kosherType?.toLowerCase() !== selectedKosher.toLowerCase()) {
                        return false;
                    }
                }

                // 5. Meal style filtering (must match ALL selected meal styles), ONLY if filterMealStyles is provided
                if (filterMealStyles.size > 0) {
                    const dinnerMealStyles = Array.isArray(dinnerDetails.mealStyle) ?
                        dinnerDetails.mealStyle.map(style => style.toLowerCase()) : [];

                    const allMealStylesMatch = Array.from(filterMealStyles).every(selectedStyle =>
                        dinnerMealStyles.includes(selectedStyle.toLowerCase())
                    );
                    if (!allMealStylesMatch) return false;
                }

                return true; // If all active criteria are met
            });

            setDinners(filtered);
        } catch (err) {
            console.error('Error searching dinners:', err);
            setError('Failed to load dinners.');
        } finally {
            setLoading(false);
        }
    }, [allDinnersData, currentUser]);

    // Initial fetch of ALL dinners from Firebase
    useEffect(() => {
        // Handle redirect result first
        const handleRedirectResult = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result) {
                    console.log('Redirect result received:', result.user.email);
                    // User successfully signed in via redirect
                }
            } catch (error) {
                console.error('Error handling redirect result:', error);
            }
        };

        handleRedirectResult();

        const fetchAllDinners = async () => {
            setLoading(true);
            setError(null);
            try {
                // Order by createdAt to show newest dinners first (most recently uploaded)
                const q = query(collection(db, 'dinners'), orderBy('createdAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const dinnersData = await Promise.all(querySnapshot.docs.map(async (docSnapshot) => {
                    const dinner = {
                        id: docSnapshot.id,
                        ...docSnapshot.data()
                    };

                    // Fetch host profile
                    if (dinner.hostId) {
                        const hostProfile = await getUserProfile(dinner.hostId);
                        dinner.hostProfile = hostProfile;
                        dinner.hostProfileImg = hostProfile?.profileImg || '';

                        // Fetch shabbot answers
                        const answerRef = doc(db, 'shabbotAnswers', dinner.hostId);
                        const answerSnap = await getDoc(answerRef);
                        if (answerSnap.exists()) {
                            dinner.shabbotAnswers = answerSnap.data().answers;
                        }
                    }
                    return dinner;
                }));

                setAllDinnersData(dinnersData);
                setDinners(dinnersData); // Set initial dinners to show all
            } catch (err) {
                console.error('Error fetching all dinners:', err);
                setError('Failed to load all dinners.');
            } finally {
                setLoading(false);
            }
        };
        fetchAllDinners();
    }, []); // Remove searchDinners dependency

    // Separate useEffect for handling search/filter updates
    useEffect(() => {
        if (allDinnersData.length > 0) {
            searchDinners(location, date, guests, currentMealStyles, currentKosherOptions);
        }
    }, [location, date, guests, currentMealStyles, currentKosherOptions, allDinnersData, currentUser, searchDinners]);

    // Prefill user languages for JoinRequestModal
    useEffect(() => {
        async function fetchUserLanguages() {
            if (!currentUser) return;
            const userRef = doc(db, 'users', currentUser.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                setUserLanguages(userSnap.data().languages || '');
            }
        }
        fetchUserLanguages();
    }, [currentUser]);

    // Add Firestore logic to fetch dinnerRequests for the current user
    useEffect(() => {
        async function fetchUserRequests() {
            if (!currentUser) return;
            const q = query(
                collection(db, 'dinnerRequests'),
                where('userId', '==', currentUser.uid)
            );
            const querySnapshot = await getDocs(q);
            const sent = new Set();
            const accepted = new Set();
            querySnapshot.forEach(doc => {
                const data = doc.data();
                if (data.status === 'pending') {
                    sent.add(data.dinnerId);
                }
                if (data.status === 'accepted') {
                    accepted.add(data.dinnerId);
                }
            });
            setSentRequests(sent);
            setAcceptedRequests(accepted);
        }
        fetchUserRequests();
    }, [currentUser]);

    // --- Searchbar Handlers ---
    const handleLocationChange = (loc) => {
        setLocation(loc);
    };

    const handleDateChange = (d) => {
        setDate(d);
    };

    const handleGuestsChange = (num) => {
        setGuests(num);
    };

    const handleSearch = () => {
        // Explicitly trigger search with current search bar and filter values
        searchDinners(location, date, guests, currentMealStyles, currentKosherOptions);
        console.log('Search button clicked, filtering triggered explicitly.');
    };

    const handleClearSearch = () => {
        setLocation('');
        setDate(null);
        setGuests(null);
        // Clear filter states as well
        setCurrentMealStyles(new Set());
        setCurrentKosherOptions(new Set());
        // Explicitly trigger search with all criteria cleared
        searchDinners('', null, null, new Set(), new Set());
    };

    // --- Filter Bar Handlers ---
    const handleMealStyleChange = (style) => {
        setCurrentMealStyles(prev => {
            const newState = new Set(prev);
            if (newState.has(style)) {
                newState.delete(style);
            } else {
                newState.add(style);
            }
            // Explicitly trigger search with updated meal styles and current search bar / other filter criteria
            searchDinners(location, date, guests, newState, currentKosherOptions);
            return newState;
        });
    };

    const handleClearMealFilters = () => {
        const clearedStyles = new Set();
        setCurrentMealStyles(clearedStyles);
        // Explicitly trigger search with cleared meal styles and current search bar / other filter criteria
        searchDinners(location, date, guests, clearedStyles, currentKosherOptions);
    };

    const handleKosherOptionChange = (option) => {
        setCurrentKosherOptions(prev => {
            const newState = new Set(prev);
            if (newState.has(option)) {
                newState.delete(option);
            } else {
                newState.add(option);
            }
            // Explicitly trigger search with updated kosher options and current search bar / other filter criteria
            searchDinners(location, date, guests, currentMealStyles, newState);
            return newState;
        });
    };

    const handleClearKosherFilters = () => {
        const clearedOptions = new Set();
        setCurrentKosherOptions(clearedOptions);
        // Explicitly trigger search with cleared kosher options and current search bar / other filter criteria
        searchDinners(location, date, guests, currentMealStyles, clearedOptions);
    };

    const handleMapToggle = () => setShowMapModal(true);

    // Add Firestore logic to onSubmitRequest
    async function handleSubmitJoinRequest(requestData) {
        if (!currentUser || !selectedHost) return;
        await createDinnerRequest({
            dinnerId: selectedHost.id,
            hostId: selectedHost.hostId,
            userId: currentUser.uid,
            guestsCount: requestData.numGuests,
            message: requestData.message,
            needs: requestData.needs,
            languages: requestData.languages
        });
        setModalState('confirmation');
        setSentRequests(prev => new Set(prev).add(selectedHost.id));
    }

    // בנה רשימת מיקומים: שכונה+עיר+מדינה וגם עיר+מדינה, וסנן ערכים בעברית
    function isHebrew(str) {
        return /[\u0590-\u05FF]/.test(str);
    }
    const availableLocations = Array.from(new Set(allDinnersData.flatMap(d => {
        const locations = [];
        if (d.address?.neighborhood && d.address?.city && d.address?.country) {
            const full = `${d.address.neighborhood}, ${d.address.city}, ${d.address.country}`;
            if (!isHebrew(full)) locations.push(full);
        }
        if (d.address?.city && d.address?.country) {
            const cityCountry = `${d.address.city}, ${d.address.country}`;
            if (!isHebrew(cityCountry)) locations.push(cityCountry);
        }
        return locations;
    })));

    // זיהוי מובייל ריאקטיבי
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- Modal handlers ---
    const handleOpenDetailModal = (dinner) => {
        setSelectedHost(dinner);
        const index = dinners.findIndex(d => d.id === dinner.id); // Find index of selected dinner
        setSelectedHostIndex(index); // Set the index
        setModalState('detail');
        // שמור אם המפה הייתה פתוחה
        setWasMapOpen(showMapModal);
    };

    const handleNavigateDinner = useCallback((direction) => {
        const newIndex = selectedHostIndex + direction;
        if (newIndex >= 0 && newIndex < dinners.length) {
            setSelectedHost(dinners[newIndex]);
            setSelectedHostIndex(newIndex);
        }
    }, [selectedHostIndex, dinners]);

    const handleOpenRequestFormDirectly = (dinner) => {
        setSelectedHost(dinner);
        setModalState('request');
    };

    const handleOpenImageModal = (url) => {
        setPreviousModalState(modalState);
        setImageModalUrl(url);
        setModalState('closed');
    };

    const handleCloseImageModal = () => {
        setImageModalUrl(null);
        if (previousModalState !== 'closed') {
            setModalState(previousModalState);
        }
    };

    const handleOpenProfileModal = async (dinner) => {
        setSelectedProfileHost(dinner);
        // שלוף את פרטי הפרופיל המלאים של ה-host
        if (dinner.hostId) {
            const profile = await getUserProfile(dinner.hostId);
            setSelectedProfileHostProfile(profile);
        } else {
            setSelectedProfileHostProfile(null);
        }
        console.log('Selected Profile Host:', dinner);
        console.log('Shabbot Answers:', dinner.shabbotAnswers);
        console.log('Fun Fact:', dinner.shabbotAnswers?.funFact);
        setShowProfileModal(true);
    };

    const handleCloseModal = () => {
        setModalState('closed');
        setSelectedHost(null);
        // אם המפה הייתה פתוחה, פתח אותה מחדש
        if (wasMapOpen) {
            setShowMapModal(true);
            setWasMapOpen(false);
        }
    };

    const handleHeaderLogoClick = () => {
        setShowMap(false);
        navigate('/home');
    };

    // --- Render ---
    // במסך הבית, סנן החוצה דינרים שאושרו
    const visibleDinners = dinners.filter(dinner => !acceptedRequests.has(dinner.id));

    return (
        <>
            {/* הסרתי את שורת ה-Header */}
            {isMobile && (
                <FilterBar
                    currentMealStyles={currentMealStyles}
                    onMealStyleChange={handleMealStyleChange}
                    onClearMealFilters={handleClearMealFilters}
                    currentKosherOptions={currentKosherOptions}
                    onKosherOptionChange={handleKosherOptionChange}
                    onClearKosherFilters={handleClearKosherFilters}
                    onMapToggle={handleMapToggle}
                    showMap={false}
                    isMobileOpen={isMobile && showMobileFilters}
                    onCloseMobile={() => setShowMobileFilters(false)}
                />
            )}
            <div className="home-page-wrapper container-fluid pb-3">
                <div className="row">
                    {/* FilterBar בדסקטופ בלבד */}
                    {!isMobile && (
                        <div className="col-lg-2 col-md-12 mb-3">
                            <FilterBar
                                currentMealStyles={currentMealStyles}
                                onMealStyleChange={handleMealStyleChange}
                                onClearMealFilters={handleClearMealFilters}
                                currentKosherOptions={currentKosherOptions}
                                onKosherOptionChange={handleKosherOptionChange}
                                onClearKosherFilters={handleClearKosherFilters}
                                onMapToggle={handleMapToggle}
                                showMap={false}
                            />
                        </div>
                    )}
                    {/* התוכן הראשי תמיד מוצג */}
                    <div className={!isMobile ? "col-lg-10 col-md-12" : "col-12"}>
                        <div className="main-content-area-with-offset" style={{padding: 30, marginTop: 66, marginLeft: -25, maxHeight: '95vh'}}>
                            <div className="searchbar-row">
                                {isMobile && (
                                    <button
                                        className="open-filters-btn"
                                        onClick={() => setShowMobileFilters(true)}
                                        aria-label="Open filters"
                                    >
                                        <svg className="sliders-icon" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <line x1="6" y1="8" x2="26" y2="8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                                            <circle cx="16" cy="8" r="3" stroke="currentColor" strokeWidth="2.5" fill="white"/>
                                            <line x1="6" y1="16" x2="26" y2="16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                                            <circle cx="10" cy="16" r="3" stroke="currentColor" strokeWidth="2.5" fill="white"/>
                                            <line x1="6" y1="24" x2="26" y2="24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                                            <circle cx="22" cy="24" r="3" stroke="currentColor" strokeWidth="2.5" fill="white"/>
                                        </svg>
                                    </button>
                                )}
                                <Searchbar
                                    location={location}
                                    date={date}
                                    guests={guests}
                                    onLocationChange={handleLocationChange}
                                    onDateChange={handleDateChange}
                                    onGuestsChange={handleGuestsChange}
                                    onSearch={handleSearch}
                                    onClearSearch={handleClearSearch}
                                    availableLocations={availableLocations}
                                />
                            </div>
                            <main className="mt-4">
                                <h2 className="available-dinners-heading mb-3">Available Dinners</h2>
                                {loading ? (
                                    <p className="text-center">Loading dinners...</p>
                                ) : error ? (
                                    <p className="text-center text-danger">Error: {error}</p>
                                ) : visibleDinners.length === 0 ? (
                                    <p className="text-center">No hosts match your criteria.</p>
                                ) : (
                                    <div className="row g-3">
                                        {visibleDinners.map((dinner) => {
                                            const isRequestSent = sentRequests.has(dinner.id);
                                            return (
                                                <div className="col-lg-6 col-12" key={dinner.id}>
                                                    <HostCard
                                                        dinner={dinner}
                                                        onCardClick={() => handleOpenDetailModal(dinner)}
                                                        onRequestJoin={() => handleOpenRequestFormDirectly(dinner)}
                                                        onImageClick={handleOpenImageModal}
                                                        isRequestSent={isRequestSent}
                                                        onProfileClick={() => handleOpenProfileModal(dinner)}
                                                        maxGuests={dinner.dinnerDetails.maxGuests}
                                                        dinnerTime={dinner.dinnerDetails.time}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </main>
                        </div>
                    </div>
                </div>
                {/* FilterBar כ-drawer במובייל */}



                {/* Modals */}
                {modalState === 'detail' && selectedHost && (
                    <HostDetailModal
                        host={selectedHost}
                        dinners={dinners}
                        currentIndex={selectedHostIndex}
                        onNavigate={handleNavigateDinner}
                        onClose={handleCloseModal}
                        onOpenRequest={() => setModalState('request')}
                        onImageClick={handleOpenImageModal}
                        isRequestSent={sentRequests.has(selectedHost?.id)}
                        onProfileClick={() => handleOpenProfileModal(selectedHost)}
                        isDanielAndSarah={selectedHost?.hostName === 'Daniel & Sarah'}
                    />
                )}

                {modalState === 'request' && selectedHost && (
                    <JoinRequestModal
                        host={selectedHost}
                        onClose={handleCloseModal}
                        onSubmitRequest={handleSubmitJoinRequest}
                        userLanguages={userLanguages}
                    />
                )}

                {modalState === 'confirmation' && (
                    <RequestSentModal
                        show={modalState === 'confirmation'}
                        onClose={handleCloseModal}
                        hostName={selectedHost?.hostProfile?.name}
                    />
                )}

                {imageModalUrl && (
                    <ImageModal
                        imageUrl={imageModalUrl}
                        onClose={handleCloseImageModal}
                    />
                )}

                {showProfileModal && selectedProfileHost && (
                    <PreviewProfileModal
                        open={showProfileModal}
                        onClose={() => setShowProfileModal(false)}
                        profile={{
                            uid: selectedProfileHostProfile?.uid || selectedProfileHost.hostId,
                            avatar: selectedProfileHostProfile?.profileImg || selectedProfileHost.hostImageUrl,
                            name: selectedProfileHostProfile?.name || selectedProfileHost.hostName,
                            city: selectedProfileHostProfile?.location?.city || selectedProfileHost.address?.city,
                            country: selectedProfileHostProfile?.location?.country || selectedProfileHost.address?.country,
                            code: getCountryCode(selectedProfileHostProfile?.location?.country || selectedProfileHost.address?.country) || '',
                            bio: selectedProfileHostProfile?.about || selectedProfileHost.description,
                            languages: selectedProfileHostProfile?.languages || '',
                            interests: selectedProfileHostProfile?.interests || (Array.isArray(selectedProfileHost.dinnerDetails?.mealStyle)
                                ? selectedProfileHost.dinnerDetails.mealStyle.join(', ')
                                : ''),
                            gallery: selectedProfileHostProfile?.gallery || selectedProfileHost.gallery || [],
                            funFact: selectedProfileHost.shabbotAnswers?.funFact || selectedProfileHost.funFact,
                            shabbotAnswers: selectedProfileHost.shabbotAnswers
                        }}
                    />
                )}

                {/* Map Modal */}
                <MapModal
                    isOpen={showMapModal}
                    onClose={() => setShowMapModal(false)}
                    dinners={dinners}
                    onDinnerClick={handleOpenDetailModal}
                    searchLocation={location}
                />
            </div>
        </>
    );
}